
import React, { useState, useEffect } from 'react';
import { VanityAPI } from '../services/api';

interface AdminLoginProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onSuccess, onCancel }) => {
  const [step, setStep] = useState<'creds' | 'otp'>('creds');
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  // Ensure database and admin user exist before login attempt
  useEffect(() => {
    VanityAPI.bootstrap();
  }, []);

  const handleCreds = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const res = await VanityAPI.login(email, pass, rememberMe);

      if (res.requireMfa && res.userId) {
        setUserId(res.userId);
        setStep('otp');
      } else {
        // Direct Success
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || "Invalid Administrative Credentials");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;

    try {
      await VanityAPI.verifyOTP(userId, otp);
      onSuccess();
    } catch (err: any) {
      setError("Verification Code Incorrect");
    }
  };

  return (
    <div className="min-h-screen bg-vanity-black flex items-center justify-center p-8 animate-fadeIn">
      <div className="max-w-md w-full bg-white p-12 shadow-3xl border-t-[10px] border-vanity-pink">
        <div className="text-center mb-12">
          <h1 className="font-serif text-5xl text-vanity-black font-bold tracking-widest uppercase">Atelier</h1>
          <p className="font-sans text-[10px] uppercase tracking-[0.5em] text-vanity-grey mt-4 font-black">Registry Management Gateway</p>
        </div>

        {error && (
          <div className="mb-8 p-4 bg-red-50 text-red-600 font-bold text-[11px] uppercase tracking-widest text-center border border-red-100 animate-pulse">
            {error}
          </div>
        )}

        {step === 'creds' ? (
          <form onSubmit={handleCreds} className="space-y-8">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[11px] font-black uppercase text-gray-400 tracking-widest">Administrator Identifier</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full border-b-2 border-gray-100 p-4 focus:border-vanity-pink outline-none font-serif text-xl"
                  placeholder="admin@vanity-magazine.at"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[11px] font-black uppercase text-gray-400 tracking-widest">Master Key</label>
                <input
                  type="password"
                  required
                  value={pass}
                  onChange={e => setPass(e.target.value)}
                  className="w-full border-b-2 border-gray-100 p-4 focus:border-vanity-pink outline-none font-serif text-xl"
                  placeholder="••••••••"
                />
              </div>
              <div className="flex items-center gap-3 pt-2">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  className="w-4 h-4 accent-vanity-pink"
                />
                <label htmlFor="rememberMe" className="text-[10px] font-black uppercase text-gray-500 tracking-widest cursor-pointer select-none">
                  Remember Me (60 Days)
                </label>
              </div>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-vanity-black text-white py-5 font-black uppercase text-[12px] tracking-widest hover:bg-vanity-pink transition-all active:scale-95 disabled:opacity-50"
            >
              {isLoading ? 'Processing...' : 'Request Registry Access'}
            </button>

          </form>
        ) : (
          <form onSubmit={handleOTP} className="space-y-8">
            <p className="text-[14px] text-gray-500 font-medium leading-relaxed text-center">
              New device detected. A verification code was sent to <span className="text-black font-bold">{email}</span>.
            </p>
            <input
              type="text"
              required
              value={otp}
              onChange={e => setOtp(e.target.value)}
              className="w-full border-b-2 border-gray-100 p-4 text-center focus:border-vanity-pink outline-none font-serif text-5xl tracking-[0.5em] text-vanity-pink"
              maxLength={6}
              placeholder="000000"
            />
            <button
              type="submit"
              className="w-full bg-vanity-black text-white py-5 font-black uppercase text-[12px] tracking-widest hover:bg-vanity-pink transition-all active:scale-95"
            >
              Confirm Identity
            </button>
            <button
              type="button"
              onClick={() => setStep('creds')}
              className="w-full text-[10px] uppercase font-black text-gray-300 tracking-widest hover:text-vanity-pink mt-4"
            >
              Return to Credentials
            </button>
          </form>
        )}

        <button
          onClick={onCancel}
          className="mt-12 w-full text-gray-300 hover:text-vanity-black font-black text-[10px] uppercase tracking-widest transition-colors"
        >
          Return to Public Anthology
        </button>
      </div>
    </div>
  );
};

export default AdminLogin;
